import * as React from 'react';

import { AriaPopoverProps } from '@react-aria/overlays';
import {
  DeductionType,
  enumDeductionApplicationType,
} from '@rematter/frontend-lib-api-client';
import { $deductionTypes } from '@rematter/modules/trade/_state/scaleTicketPro.state';
import { cn, Menu, TouchButton, TouchButtonProps } from '@rematter/ui-web';
import { useAtomValue } from 'jotai';
import { LucideChevronUp } from 'lucide-react';

import { useTranslations } from '~/modules/trade/i18n/useTranslations';

interface Props {
  className?: string;
  label?: React.ReactNode;
  onAddDeduction: (deduction: {
    typeId: string;
    typeName: string;
    amount: number;
    isWeightBased: boolean;
  }) => void;
  buttonSize?: 'sm' | 'md' | 'lg' | 'xl';
  placement?: AriaPopoverProps['placement'];
  variant?: TouchButtonProps['variant'];
}

//
// ⚛️ ⚛️ ⚛️ ADD DEDUCTION FIELD ⚛️ ⚛️ ⚛️
//
export const AddDeductionField: React.FC<Props> = ({
  className,
  label,
  onAddDeduction,
  buttonSize,
  placement,
  variant,
}) => {
  // Translation
  const t = useTranslations();

  // States
  const [isOpenList, setIsOpenList] = React.useState(false);

  // Atoms
  const deductionTypesQuery = useAtomValue($deductionTypes);
  const deductionTypes = (deductionTypesQuery.data ?? []) as DeductionType[];

  // Callbacks
  const handleSelectOption = React.useCallback((option: DeductionType) => {
    onAddDeduction({
      typeId: option.id,
      typeName: option.name,
      amount: 0,
      isWeightBased:
        option.applicationType === enumDeductionApplicationType.Weight,
    });
  }, []);

  // Memoized values
  const deductionOptions = React.useMemo(
    () =>
      deductionTypes.map((option) => ({
        label: option.name,
        onAction: () => handleSelectOption(option),
      })),
    [deductionTypes, handleSelectOption],
  );

  return (
    <Menu
      items={deductionOptions}
      placement={placement || 'top right'}
      onOpenChange={() => setIsOpenList(!isOpenList)}
      menuClassName={cn('min-w-50 [&>*]:!min-h-15', className)}
      trigger={
        <TouchButton
          variant={variant || 'outline'}
          color="failure"
          className="border-2 border-failure"
          isSelected={false}
          size={buttonSize}
          onPress={() => null}
        >
          {label || (
            <>
              {t.scaleTicketPro.actions.addDeduction}{' '}
              <LucideChevronUp className={cn(isOpenList && 'scale-[-1]')} />
            </>
          )}
        </TouchButton>
      }
    />
  );
};
